import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, Mail, Facebook, MapPin } from "lucide-react";

import { BUSINESS, cities, cityLink } from "@/lib/site";
import { IMAGES } from "@/lib/images";
import { JsonLd } from "@/components/site/JsonLd";
import { localBusinessSchema, breadcrumbSchema } from "@/lib/schema";
import { FadeUp } from "@/components/site/FadeUp";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { InnerHero } from "@/components/site/PageBits";
import { LazyQuoteForm } from "@/components/site/LazyQuoteForm";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact | Pretty in Pink Cleaning Service" },
      {
        name: "description",
        content:
          "Request a quote from Pretty in Pink Cleaning Service in Wetumpka, AL — fill out our form, or call (334) 202-3594 for residential and commercial cleaning.",
      },
      { property: "og:title", content: "Contact | Pretty in Pink Cleaning Service" },
      {
        property: "og:description",
        content:
          "Request a quote from Pretty in Pink Cleaning Service in Wetumpka, AL by form, phone, or email.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: IMAGES.coverBanner },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: IMAGES.coverBanner },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <>
      <JsonLd data={localBusinessSchema()} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ])}
      />
      <InnerHero
        eyebrow="Contact"
        title="Let's get you a pretty clean"
        subtitle="Reach out any way that's easiest — fill out our quote form, call, or email us. We serve Wetumpka and the surrounding areas."
      >
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "Contact", path: "/contact" },
            ]}
          />
        </div>
      </InnerHero>

      {/* Quote form (id="quote") + quick contact details */}
      <section id="quote" className="scroll-mt-24 px-4 py-12 md:px-6 md:py-16">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <FadeUp>
              <p className="eyebrow">Request a Quote</p>
              <h2 className="mt-1 text-2xl font-bold text-ink text-balance md:text-3xl">
                Tell us about your space
              </h2>
              <p className="mt-3 max-w-xl text-muted-foreground text-pretty">
                Fill out the form and we&rsquo;ll put together a quote — no obligation. Tell us the
                size and type of space and what you&rsquo;d like cleaned, and we&rsquo;ll be in
                touch by your preferred method.
              </p>
            </FadeUp>
            <div className="mt-8">
              <LazyQuoteForm page="Contact" />
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <FadeUp>
              <a
                href={BUSINESS.phoneHref}
                className="lift-card flex h-full items-start gap-4 rounded-2xl border border-border bg-card p-6"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Phone className="h-6 w-6" />
                </span>
                <span>
                  <span className="block font-display text-xs font-bold uppercase tracking-wider text-primary">
                    Call us
                  </span>
                  <span className="mt-1 block text-lg font-bold text-ink">
                    {BUSINESS.phoneDisplay}
                  </span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    Tap to call — we answer during business hours.
                  </span>
                </span>
              </a>
            </FadeUp>

            <FadeUp delay={60}>
              <a
                href={BUSINESS.emailHref}
                className="lift-card flex h-full items-start gap-4 rounded-2xl border border-border bg-card p-6"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Mail className="h-6 w-6" />
                </span>
                <span>
                  <span className="block font-display text-xs font-bold uppercase tracking-wider text-primary">
                    Email us
                  </span>
                  <span className="mt-1 block break-all text-lg font-bold text-ink">
                    {BUSINESS.email}
                  </span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    Send us the details and we&rsquo;ll reply.
                  </span>
                </span>
              </a>
            </FadeUp>

            <FadeUp delay={120}>
              <a
                href={BUSINESS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="lift-card flex h-full items-start gap-4 rounded-2xl border border-border bg-card p-6"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Facebook className="h-6 w-6" />
                </span>
                <span>
                  <span className="block font-display text-xs font-bold uppercase tracking-wider text-primary">
                    Facebook
                  </span>
                  <span className="mt-1 block text-lg font-bold text-ink">
                    Message us on Facebook
                  </span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    Find us and send a message anytime.
                  </span>
                </span>
              </a>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Service area */}
      <section className="bg-secondary/40 px-4 py-12 md:px-6 md:py-16">
        <FadeUp>
          <SectionHeading
            eyebrow="Service Area"
            title="Where we clean"
            intro="We're based in Wetumpka and serve the surrounding River Region. Find your city below."
          />
        </FadeUp>
        <div className="mx-auto mt-8 flex max-w-4xl flex-wrap justify-center gap-3">
          {cities.map((c) => (
            <Link
              key={c.slug}
              {...cityLink(c.slug)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-ink transition-colors hover:border-primary hover:text-primary"
            >
              <MapPin className="h-4 w-4 text-primary" />
              {c.name}, {c.state}
            </Link>
          ))}
        </div>
      </section>

      <section className="px-4 py-12 md:px-6 md:py-16">
        <FadeUp className="mx-auto max-w-3xl rounded-3xl border border-border bg-secondary/50 p-8 text-center md:p-12">
          <h2 className="text-2xl font-bold text-ink md:text-3xl text-balance">
            Prefer to talk it through?
          </h2>
          <p className="mt-3 text-muted-foreground text-pretty">
            Call us at {BUSINESS.phoneDisplay} and we'll walk you through how it works and put
            together a quote for your space.
          </p>
          <a
            href={BUSINESS.phoneHref}
            className="btn-sparkle mt-6 inline-flex items-center justify-center gap-2 bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
          >
            <span>Call {BUSINESS.phoneDisplay}</span>
          </a>
        </FadeUp>
      </section>
    </>
  );
}
